import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Facebook,
  Instagram,
  MessageSquare,
  Shield,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Users,
  Trash2,
  XCircle,
  Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useApi, API_ENDPOINTS } from '../lib/api';

// Interface for Facebook Page Data
interface FacebookPageData {
  id: string;
  name: string;
  platform?: "facebook" | "instagram";
  category: string;
  followers: string;
  status: string;
  lastActivity: string;
  picture?: string;
  username?: string;
  publicLink?: string;
  prompt?: string;
  aiSettings: {
    autoRepliesEnabled: boolean;
    languagePreference: 'derja' | 'derja_latin' | 'french' | 'arabic' | 'auto' | 'en';
    replyDelay: number; // -1 represents "real" mode with random delay
  };
}

const FacebookPageDetail: React.FC = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const api = useApi();

  // State for Page Data
  const [page, setPage] = useState<FacebookPageData | null>(null);
  const isInstagram = page?.platform === "instagram";
  const platformName = isInstagram ? "Instagram" : "Facebook";
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false);

  // State for Activity Stats
  const [stats, setStats] = useState({
    autoRepliesSent: 0,
    commentsModerated: 0,
    lastMessageDate: 'N/A'
  });

  // Fetch page data on component mount
  useEffect(() => {
    if (pageId) {
      fetchPageData();
      fetchActivityStats();
    }
  }, [pageId]);

  const fetchPageData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get(API_ENDPOINTS.FACEBOOK_PAGE_DETAIL(pageId!));
      const pageData: FacebookPageData = response.data;
      setPage(pageData);
    } catch (err: any) {
      console.error('Error fetching page data:', err);
      setError(err.response?.data?.error || 'Failed to load page data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchActivityStats = async () => {
    try {
      const dashboardStatsResponse = await api.get(API_ENDPOINTS.DASHBOARD_STATS);
      const aiAnalyticsResponse = await api.get(API_ENDPOINTS.AI_ANALYTICS(pageId!));

      setStats({
        autoRepliesSent: aiAnalyticsResponse.data?.totalAIResponses || 0,
        commentsModerated: dashboardStatsResponse.data?.moderatedComments || 0,
        lastMessageDate: page?.lastActivity || 'N/A'
      });
    } catch (err) {
      console.error('Error fetching activity stats:', err);
    }
  };

  const handleDisconnectPage = async () => {
    if (!pageId || !page) return;

    const confirmed = window.confirm(
      `Disconnect "${page.name}" from DeepChat?\n\n` +
      `This will:\n` +
      `• Hide the page from your dashboard\n` +
      `• Preserve all data and settings\n\n` +
      `You can reconnect anytime. Continue?`
    );

    if (!confirmed) return;

    try {
      setIsLoading(true);
      setIsManageDialogOpen(false);
      await api.post(API_ENDPOINTS.FACEBOOK_PAGE_DEACTIVATE(pageId), {});
      navigate('/accounts');
    } catch (err: any) {
      console.error('Error disconnecting page:', err);
      alert(`Failed to disconnect page: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePermanentDeletePage = async () => {
    if (!pageId || !page) return;

    // First confirmation
    const firstConfirm = window.confirm(
      `⚠️ PERMANENTLY DELETE "${page.name}"?\n\n` +
      `This action CANNOT be undone and will:\n` +
      `• Delete ALL conversations and messages\n` +
      `• Delete ALL comments and posts\n` +
      `• Delete ALL customer profiles\n` +
      `• Delete ALL settings and AI configurations\n` +
      `• Remove the page from your account\n\n` +
      `Are you absolutely sure?`
    );

    if (!firstConfirm) return;

    // Second confirmation
    const secondConfirm = window.confirm(
      `⚠️ FINAL WARNING ⚠️\n\n` +
      `You are about to PERMANENTLY DELETE all data for "${page.name}".\n\n` +
      `This action is irreversible.\n\n` +
      `Click OK to proceed with permanent deletion.`
    );

    if (!secondConfirm) return;

    try {
      setIsLoading(true);
      setIsManageDialogOpen(false);
      const response = await api.delete(API_ENDPOINTS.FACEBOOK_PAGE_PERMANENT_DELETE(pageId));
      
      if (response.data.success) {
        const log = response.data.deletionLog;
        alert(
          `✓ Page permanently deleted\n\n` +
          `Deleted data:\n` +
          `• Conversations: ${log.conversations}\n` +
          `• Messages: ${log.messages}\n` +
          `• Comments: ${log.comments}\n` +
          `• Posts: ${log.posts}\n` +
          `• Customer Profiles: ${log.customerProfiles}`
        );
        navigate('/accounts');
      }
    } catch (err: any) {
      console.error('Error permanently deleting page:', err);
      alert(`Failed to permanently delete page: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Active
          </Badge>
        );
      default:
        return (
          <Badge className="bg-gray-100 text-gray-800 border-gray-200">
            <AlertTriangle className="h-3 w-3 mr-1" />
            {status}
          </Badge>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading page...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Button
                onClick={() => navigate('/accounts')}
                variant="ghost"
                size="sm"
                className="mr-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Pages
              </Button>
              <div className="flex items-center">
                <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center overflow-hidden mr-4">
                  {page?.picture ? (
                    <img 
                      src={page.picture} 
                      alt={`${page.name} logo`}
                      className="w-full h-full object-cover"
                    />
                  ) : isInstagram ? (
                    <Instagram className="h-8 w-8 text-white" />
                  ) : (
                    <Facebook className="h-8 w-8 text-white" />
                  )}
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{page?.name}</h1>
                  <div className="flex items-center space-x-4 mt-2">
                    <p className="text-gray-600">{page?.category}</p>
                    {getStatusBadge(page?.status || 'unknown')}
                  </div>
                </div>
              </div>
            </div>
            
            <Button
              onClick={() => setIsManageDialogOpen(true)}
              variant="outline"
              size="sm"
              className="text-red-600 border-red-300 hover:bg-red-50"
              disabled={isLoading}
              title="Remove page options"
            >
              <Settings className="h-4 w-4 mr-2" />
              Remove Page
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Page Statistics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Users className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Followers</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {page?.followers != null && page?.followers !== '' ? page.followers : '—'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <MessageSquare className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">AI Replies Sent</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.autoRepliesSent}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Shield className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Comments Moderated</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.commentsModerated}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>

          {/* Feature Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Shield className="h-6 w-6 text-purple-600" />
                  <span>Page Features</span>
                </CardTitle>
                <CardDescription>
                  Manage your automated customer service features for this {platformName} {isInstagram ? "account" : "page"}.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                  {/* COMMENTED OUT FOR FACEBOOK REVIEW - Only submitting comment moderation feature */}
                  {/* Message Autoreply */}
                  {/* <div className="p-6 border-2 border-gray-200 rounded-xl hover:border-blue-300 transition-colors">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                        <MessageSquare className="h-8 w-8 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">Message Autoreply</h3>
                        <p className="text-gray-600">AI-powered customer service</p>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-6">
                      Configure AI responses for private Messenger conversations. Set up personality, 
                      language preferences, and provide knowledge base content for better responses.
                    </p>
                    <Button
                      onClick={() => navigate(`/accounts/${pageId}/autoreply`)}
                      size="lg"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg"
                    >
                      <MessageSquare className="h-5 w-5 mr-3" />
                      Configure Message Autoreply
                    </Button>
                  </div> */}

                  {/* Comment Moderation */}
                  <div className="p-6 border-2 border-gray-200 rounded-xl hover:border-purple-300 transition-colors">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center">
                        <Shield className="h-8 w-8 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">Comment Moderation</h3>
                        <p className="text-gray-600">Automated content filtering</p>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-6">
                      Set up automated rules to moderate comments on your posts. Filter inappropriate 
                      content, spam, and manage your community interactions effectively.
                    </p>
                    <Button
                      onClick={() => navigate(`/accounts/${pageId}/moderation`)}
                      size="lg"
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 text-lg"
                    >
                      <Shield className="h-5 w-5 mr-3" />
                      Configure Comment Moderation
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>

      {/* Remove Page Dialog */}
      <Dialog open={isManageDialogOpen} onOpenChange={setIsManageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Page: {page?.name}</DialogTitle>
          </DialogHeader>
          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-600 mb-6">
              Choose an action for this {platformName} {isInstagram ? "account" : "page"}:
            </p>
            
            {/* Disconnect Option */}
            <div className="border border-orange-200 rounded-lg p-4 hover:bg-orange-50 transition-colors">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <XCircle className="h-6 w-6 text-orange-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Disconnect Page
                  </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Hide this page from your dashboard while preserving all data. You can reconnect anytime.
                    </p>
                    <ul className="text-xs text-gray-500 space-y-1 mb-4">
                      <li>✓ All data and settings preserved</li>
                      <li>✓ Can be restored by reconnecting</li>
                    </ul>
                  <Button
                    onClick={handleDisconnectPage}
                    variant="outline"
                    className="w-full text-orange-600 border-orange-300 hover:bg-orange-50"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <XCircle className="h-4 w-4 mr-2" />
                    )}
                    Disconnect Page
                  </Button>
                </div>
              </div>
            </div>

            {/* Delete Option */}
            <div className="border border-red-200 rounded-lg p-4 hover:bg-red-50 transition-colors">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Delete Permanently
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Permanently remove this page and all associated data. This action cannot be undone.
                  </p>
                  <ul className="text-xs text-gray-500 space-y-1 mb-4">
                    <li>⚠️ All conversations deleted</li>
                    <li>⚠️ All comments and posts removed</li>
                    <li>⚠️ Cannot be recovered</li>
                  </ul>
                  <Button
                    onClick={handlePermanentDeletePage}
                    variant="outline"
                    className="w-full text-red-600 border-red-300 hover:bg-red-50"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    Delete Permanently
                  </Button>
                </div>
              </div>
            </div>

            {/* Cancel Button */}
            <Button
              onClick={() => setIsManageDialogOpen(false)}
              variant="outline"
              className="w-full mt-4"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FacebookPageDetail; 